"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "react-router-dom"
import axios from "axios"
import { API_URL } from "../config"
import { useLanguage } from "../context/LanguageContext"
import { useNotification } from "../context/NotificationContext"
import "./ProjectDetails.css"
import ETrefCalculationModal from "../components/calculator/ETrefCalculationModal"
import { fetchWeatherDataFormatted } from "../services/getWeatherData" // <-- import
import Papa from "papaparse"
import Modal from "../components/Modal"
import {generateWeatherData} from "../services/getWeatherDataUtil";


const ProjectDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()

  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [cropDetails, setCropDetails] = useState(null)
  const [etrefCalculations, setEtrefCalculations] = useState([])
  const [etplantCalculations, setEtplantCalculations] = useState([])
  const [calculationsLoading, setCalculationsLoading] = useState(false)
  const [showCalculations, setShowCalculations] = useState(false)
  const [kcValues, setKcValues] = useState([])
  const [showETrefModal, setShowETrefModal] = useState(false)
  const [contentExpanded, setContentExpanded] = useState(false)
  const [calculationInProgress, setCalculationInProgress] = useState(false)
  const [activeTab, setActiveTab] = useState("overview")
  const [parameterGroupDetails, setParameterGroupDetails] = useState(null); // Add state for parameterGroupDetails
  const [fetchedWeatherParams, setFetchedWeatherParams] = useState(null); // <-- add state

  // CSV upload state
  const [csvData, setCsvData] = useState(null)
  const [csvError, setCsvError] = useState(null)
  const [showCsvModal, setShowCsvModal] = useState(false)

  // Forecasting Parameter Modal state
  const [showForecastModal, setShowForecastModal] = useState(false);
  const [forecastData, setForecastData] = useState([]);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastAvgYears, setForecastAvgYears] = useState(5);

  // Add new state for submit loading
  const [csvSubmitLoading, setCsvSubmitLoading] = useState(false);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log("Formatted Dates:", project?.plantingDate, project?.harvestDate);

        const response = await axios.get(`${API_URL}/api/projects/${id}`);
        setCropDetails(response.data.cropDetails);
        setProject(response.data);

        // Fetch parameterGroupDetails if available in the response
        if (response.data.parameterGroupDetails) {
          setParameterGroupDetails(response.data.parameterGroupDetails);
        }


        // Fetch calculations and KC values
        await fetchCalculations();
        await fetchKcValues();

        setLoading(false);
      } catch (err) {
        console.error("Error fetching project:", err);
        setError(err.response?.data?.msg || "Failed to load project details");
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);
  const fetchKcValues = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/${id}/kc-values`)
      setKcValues(response.data)
    } catch (err) {
      console.error("Error fetching KC values:", err)
    }
  }


// Handler to open modal and fetch data
  const handleOpenForecastModal = async () => {
    setShowForecastModal(true);
    setForecastLoading(true);
    try {
      const start = new Date(project.plantingDate).toISOString().split("T")[0];
      const end = new Date(project.harvestDate).toISOString().split("T")[0];
      console.log()
      const data = await generateWeatherData(start, end, forecastAvgYears, project.parameterGroupDetails.location.coordinates[1],  project.parameterGroupDetails.location.coordinates[0]);

      setForecastData(data);
    } catch (err) {
      setForecastData([]);
    }
    setForecastLoading(false);
  };

// Handler for changing average years
  const handleForecastAvgYearsChange = async (e) => {
    const years = Number(e.target.value);
    setForecastAvgYears(years);
    setForecastLoading(true);
    try {
      const start = new Date(project.plantingDate).toISOString().split("T")[0];
      const end = new Date(project.harvestDate).toISOString().split("T")[0];
      const data = await generateWeatherData(start, end, years);
      setForecastData(data);
    } catch (err) {
      setForecastData([]);
    }
    setForecastLoading(false);
  };


  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await axios.get(`${API_URL}/api/projects/${id}`)
        setCropDetails(response.data.cropDetails)
        setProject(response.data)

        // Fetch calculations and KC values
        await fetchCalculations()
        await fetchKcValues()

        // If project has a cropType ID, fetch the complete crop details
        if (response.data.cropType && response.data.cropType._id) {
          try {
            const cropTypeId = response.data.cropType._id
            // First try to fetch the crop if it exists
            if (response.data.crop && response.data.crop._id) {
              const cropResponse = await axios.get(`${API_URL}/api/crops/${response.data.crop._id}`)
              setCropDetails(project.cropDetails)
            } else {
              // If no specific crop, fetch crops for this crop type
              const cropsResponse = await axios.get(`${API_URL}/api/crops/by-crop-type/${cropTypeId}`)
              if (cropsResponse.data && cropsResponse.data.length > 0) {
                setCropDetails(cropsResponse.data[0]) // Use the first crop as default
              }
            }
          } catch (cropErr) {
            console.error("Error fetching crop details:", cropErr)
          }
        }

        setLoading(false)
      } catch (err) {
        console.error("Error fetching project:", err)
        setError(err.response?.data?.msg || "Failed to load project details")
        setLoading(false)
      }
    }

    fetchProject()
  }, [id])

  const handleDelete = async () => {
    if (!window.confirm(t("Are you sure you want to delete this project?"))) {
      return
    }

    try {
      await axios.delete(`${API_URL}/api/projects/${id}`)
      showNotification(t("Project deleted successfully"), "success")
      navigate("/dashboard")
    } catch (err) {
      console.error("Error deleting project:", err)
      showNotification(err.response?.data?.msg || "Failed to delete project", "error")
    }
  }

  const fetchCalculations = async () => {
    try {

      setCalculationsLoading(true)
      const response = await axios.get(`${API_URL}/api/projects/${id}/calculations`)
      setEtrefCalculations(response.data.etref || [])
      setEtplantCalculations(response.data.etplant || [])
    } catch (err) {
      console.error("Error fetching calculations:", err)
      showNotification(t("Failed to fetch calculations"), "error")
    } finally {
      setCalculationsLoading(false)
    }
  }

  const calculateETref = async () => {
    setShowETrefModal(true)
  }

  const handleETrefCalculation = async (calculationData) => {
    try {
      setCalculationInProgress(true);
      setShowETrefModal(false);

      if (!parameterGroupDetails || !parameterGroupDetails.location) {
        showNotification(t("Parameter Station details are missing"), "error");
        return;
      }

      // Extract location from parameterGroupDetails
      const latitude = parameterGroupDetails.location.coordinates[1];
      const longitude = parameterGroupDetails.location.coordinates[0];

      // Prepare the request data (only required fields)
      const requestData = {
        projectId: id,
        models: calculationData.models,
        dateMode: calculationData.dateMode,
        date: calculationData.date,
        dateRange: calculationData.dateRange,
        dateType: calculationData.dateMode === "range" ? "range" : "single",
        hcref: project.hcref || 0.2,
        location: {
          coordinates: {
            lat: latitude,
            lng: longitude,
          },
        },
      };

      // Make the API call
      await axios.post(`${API_URL}/api/projects/${id}/calculate-etref`, requestData);

      // Refresh calculations
      await fetchCalculations();

      showNotification(t("WATref calculated successfully"), "success");

      setActiveTab("calculations");
      setShowCalculations(true);
    } catch (err) {
      console.error("Error calculating WATref:", err);
      showNotification(err.response?.data?.message || t("Failed to calculate WATref"), "error");
    } finally {
      setCalculationInProgress(false);
    }
  };

  const calculateETplant = async () => {
    try {
      setCalculationsLoading(true)
      // Prepare today's date in ISO format (yyyy-mm-dd)
      const today = new Date().toISOString().slice(0, 10)
      await axios.post(`${API_URL}/api/projects/${id}/calculate-etplant`, {
        projectId: id,
        date: today,
      })
      await fetchCalculations()
      showNotification(t("ETplant calculated successfully"), "success")

      // Show calculations tab after successful calculation
      setActiveTab("calculations")
      setShowCalculations(true)
    } catch (err) {
      console.error("Error calculating ETplant:", err)
      showNotification(err.response?.data?.message || t("Failed to calculate ETplant"), "error")
    } finally {
      setCalculationsLoading(false)
    }
  }

  const recalculateAll = async () => {
    try {
      setCalculationsLoading(true)
      await axios.post(`${API_URL}/api/projects/${id}/recalculate`)
      await fetchCalculations()
      showNotification(t("All calculations updated successfully"), "success")
    } catch (err) {
      console.error("Error recalculating:", err)
      showNotification(err.response?.data?.message || t("Failed to recalculate"), "error")
    } finally {
      setCalculationsLoading(false)
    }
  }

  // Helper to get all required dates from project start to end date
  const getRequiredDates = () => {
    if (!project?.plantingDate || !project?.harvestDate) return [];
    const startDate = new Date(project.plantingDate);
    const endDate = new Date(project.harvestDate);

    const dates = [];
    let currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().slice(0, 10));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  }
  // Generate sample CSV for selected parameters, with all required dates and empty values
  const getSampleCsv = () => {
    // Collect all selected parameters from all devices
    const selectedParameters =
      parameterGroupDetails?.selectedDevices?.flatMap(device =>
        device.selectedParameters?.length > 0 ? device.selectedParameters : []
      ) || [];

    if (!selectedParameters.length) return "";

    const headers = ["date", ...selectedParameters];
    const requiredDates = getRequiredDates();

    if (requiredDates.length === 0) return "";

    // Remove example values, leave cells empty for user input
    const rows = requiredDates.map((date) => {
      const values = selectedParameters.map(() => "");
      return [date, ...values].join(",");
    });

    return `${headers.join(",")}\n${rows.join("\n")}`;
  };

  // Handle CSV file upload
  const handleCsvUpload = (e) => {
    setCsvError(null);
    const file = e.target.files[0];
    if (!file) return;

    // Collect all selected parameters from all devices
    const selectedParameters =
      parameterGroupDetails?.selectedDevices?.flatMap(device =>
        device.selectedParameters?.length > 0 ? device.selectedParameters : []
      ) || [];

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data;
        const expectedDates = getRequiredDates();
        const requiredHeaders = ["date", ...selectedParameters];

        // Validate row count
        if (data.length !== expectedDates.length) {
          setCsvError(`Expected ${expectedDates.length} rows, but got ${data.length}.`);
          setCsvData(null);
          return;
        }

        // Validate required headers exist
        const uploadedHeaders = Object.keys(data[0]);
        for (let header of requiredHeaders) {
          if (!uploadedHeaders.includes(header)) {
            setCsvError(`Missing required column: "${header}"`);
            setCsvData(null);
            return;
          }
        }

        // Validate sequential dates
        for (let i = 0; i < expectedDates.length; i++) {
          if (data[i].date !== expectedDates[i]) {
            setCsvError(`Date mismatch at row ${i + 1}: expected "${expectedDates[i]}", got "${data[i].date}"`);
            setCsvData(null);
            return;
          }
        }

        setCsvData(data);
      },
      error: (err) => {
        setCsvError("Failed to parse CSV: " + err.message);
        setCsvData(null);
      }
    });
  };


  // Fetch forecasting parameter data
  const handleViewForecastParams = async (avgYears = forecastAvgYears) => {
    if (!parameterGroupDetails?.location?.coordinates?.length) return;
    setForecastLoading(true);
    setShowForecastModal(true);

    // Use Parameter Station Details location
    const latitude = parameterGroupDetails.location.coordinates[1];
    const longitude = parameterGroupDetails.location.coordinates[0];
    const startDate = project?.plantingDate;
    const endDate = project?.harvestDate;

    try {
      const formattedStart = new Date(startDate).toISOString().slice(0, 10);
      const formattedEnd = new Date(endDate).toISOString().slice(0, 10);
      const data = await generateWeatherData(formattedStart, formattedEnd, avgYears, latitude, longitude);
      setForecastData(data);
    } catch (err) {
      setForecastData([]);
    } finally {
      setForecastLoading(false);
    }
  };

  // Add new handler to submit CSV data combined with forecasting parameter data
  const handleSubmitCsvData = async () => {
    if (!csvData || !forecastData || !parameterGroupDetails) return;
    setCsvSubmitLoading(true);

    try {
      // Collect all selected parameters from all devices
      const selectedParameters =
        parameterGroupDetails?.selectedDevices?.flatMap(device =>
          device.selectedParameters?.length > 0 ? device.selectedParameters : []
        ) || [];

      // Merge CSV and forecasting data by date, prioritizing CSV values for selected parameters
      const combinedData = forecastData.map((forecastRow) => {
        const csvRow = csvData.find((row) => row.date === forecastRow.date) || {};
        const mergedRow = { ...forecastRow };

        // For each selected parameter, use CSV value if present and not empty, else use forecast value
        selectedParameters.forEach((param) => {
          if (csvRow[param] !== undefined && csvRow[param] !== "") {
            mergedRow[param] = csvRow[param];
          }
        });


        // Always keep the date field
        mergedRow.date = forecastRow.date;
        return mergedRow;
      });

      // Send to backend
      await axios.post(`${API_URL}/api/projects/${id}/upload-station-parameter-data`, {
        parameterGroupId: parameterGroupDetails._id,
        data: combinedData,
      });

      showNotification(t("Station parameter data uploaded successfully"), "success");
    } catch (err) {
      showNotification(
        err.response?.data?.message || t("Failed to upload station parameter data"),
        "error"
      );
    } finally {
      setCsvSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="project-details-loading">
        <div className="spinner-container">
          <div className="spinner-large"></div>
          <p>{t("Loading project details...")}</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="project-details-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{t(error)}</p>
        <button onClick={() => navigate("/dashboard")} className="back-button">
          {t("Back to Dashboard")}
        </button>
      </div>
    )
  }

  if (!project) {
    return (
      <div className="project-details-error">
        <i className="fas fa-exclamation-circle"></i>
        <p>{t("Project not found")}</p>
        <button onClick={() => navigate("/dashboard")} className="back-button">
          {t("Back to Dashboard")}
        </button>
      </div>
    )
  }

  // Determine crop type details - use fetched cropDetails if available
  const cropTypeDetails = cropDetails
    ? {
      name: cropDetails.name || (project.cropType ? project.cropType.name : ""),
      crop: cropDetails.crop || (project.cropType ? project.cropType.crop : ""),
      growthStages: cropDetails.growthStages || (project.cropType ? project.cropType.growthStages : {}),
      total: cropDetails.total || (project.cropType ? project.cropType.total : 0),
      plantDate: cropDetails.plantDate || project.plantDate,
      region: cropDetails.region || (project.cropType ? project.cropType.region : ""),
      cropCoefficients: cropDetails.cropCoefficients || (project.cropType ? project.cropType.cropCoefficients : {}),
    }
    : project.cropType
      ? {
        name: project.cropType.name,
        crop: project.cropType.crop,
        growthStages: project.cropType.growthStages,
        total: project.cropType.total,
        plantDate: project.cropType.plantDate || project.plantDate,
        region: project.cropType.region,
        cropCoefficients: project.cropType.cropCoefficients,
      }
      : project.customCropType
        ? {
          name: project.customCropType.cropType,
          crop: project.customCropType.crop,
          growthStages: project.customCropType.growthStages,
          total: project.customCropType.total,
          plantDate: project.customCropType.plantDate || project.plantDate,
          region: project.customCropType.region,
          cropCoefficients: project.customCropType.cropCoefficients,
        }
        : null

  // Determine soil type details
  const soilTypeDetails = project.soilType
    ? {
      name: project.soilType.name,
      description: project.soilType.description,
      properties: project.soilType.properties,
    }
    : project.customSoilType
      ? {
        name: project.customSoilType.name,
        description: project.customSoilType.description,
        properties: project.customSoilType.properties,
      }
      : null

  // Get project status class and icon
  const getStatusInfo = (status) => {
    switch (status.toLowerCase()) {
      case "active":
        return { class: "status-active", icon: "fa-play-circle" }
      case "completed":
        return { class: "status-completed", icon: "fa-check-circle" }
      case "planning":
        return { class: "status-planning", icon: "fa-calendar-alt" }
      case "paused":
        return { class: "status-paused", icon: "fa-pause-circle" }
      default:
        return { class: "status-default", icon: "fa-info-circle" }
    }
  }

  const statusInfo = getStatusInfo(project.status)

  return (
    <div className="project-details-container" dir={direction}>
      {/* Project Header */}
      <div className="project-header">
        <div className="project-header-content">
          <div className="project-title-section">
            {/* Project Title with Label */}
            <div className="title-with-label">
              <div className="section-label">{t("Field Name")}</div>
              <h1 className="project-title">{project.name}</h1>
            </div>

            {/* Project Status with Label */}
            <div className="status-with-label">
              <div className="section-label">{t("Field Status")}</div>
              <div className={`project-status ${statusInfo.class}`}>
                <i className={`fas ${statusInfo.icon}`}></i>
                <span>{t(project.status.charAt(0).toUpperCase() + project.status.slice(1))}</span>
              </div>
            </div>
          </div>

          {/* Project Description with Label */}
          <div className="description-with-label">
            <div className="section-label">{t("Description")}</div>
            <p className="project-description">
              {project.description || <span className="no-description">{t("No description provided")}</span>}
            </p>
          </div>

          <div className="project-meta-info">
            <div className="meta-grid">
              {/* Planting Date */}
              <div className="meta-item">
                <div className="meta-label">{t("Start Date")}</div>
                <div className="meta-value">
                  <i className="fas fa-calendar-day icon"></i>
                  <span>{new Date(project.plantingDate).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Harvest Date */}
              {project.harvestDate && (
                <div className="meta-item">
                  <div className="meta-label">{t("End Date")}</div>
                  <div className="meta-value">
                    <i className="fas fa-calendar-check icon"></i>
                    <span>{new Date(project.harvestDate).toLocaleDateString()}</span>
                  </div>
                </div>
              )}

              {/* Farm Location */}
              <div className="meta-item">
                <div className="meta-label">{t("Location")}</div>
                <div className="meta-value">
                  <i className="fas fa-map-marker-alt icon"></i>
                  <span>{project.farm.name}</span>
                </div>
              </div>

              {/* Crop Type */}
              <div className="meta-item">
                <div className="meta-label">{t("Crop Type")}</div>
                <div className="meta-value">
                  <i className="fas fa-seedling icon"></i>
                  <span>{cropTypeDetails?.name || t("No crop specified")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="project-actions">
          <div className="action-buttons">
            {/* <Link to={`/projects/${id}/edit`} className="action-button edit-button">
              <i className="fas fa-edit"></i>
              <span>{t("Edit")}</span>
            </Link> */}
            {/* <button onClick={handleDelete} className="action-button delete-button">
              <i className="fas fa-trash"></i>
              <span>{t("Delete")}</span>
            </button> */}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="project-tabs">
        <button
          className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <i className="fas fa-info-circle"></i>
          <span>{t("Overview")}</span>
        </button>
        <button
          className={`tab-button ${activeTab === "calculations" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("calculations")
            setShowCalculations(true)
          }}
        >
          <i className="fas fa-calculator"></i>
          <span>{t("Calculations")}</span>
        </button>
        <button className={`tab-button ${activeTab === "kc" ? "active" : ""}`} onClick={() => setActiveTab("kc")}>
          <i className="fas fa-chart-line"></i>
          <span>{t("Kplant Values")}</span>
        </button>
      </div>

      {/* Quick Action Buttons */}
      <div className="quick-actions">
        <button onClick={calculateETref} className="quick-action-button etref-button" disabled={calculationInProgress}>
          <i className="fas fa-calculator"></i>
          <span>{t("Calculate WATref")}</span>
        </button>
        <button
          onClick={calculateETplant}
          className="quick-action-button etplant-button"
          disabled={calculationsLoading}
        >
          <i className="fas fa-leaf"></i>
          <span>{t("Calculate ETplant")}</span>
        </button>
        <Link to={`/projects/${id}/kc-calculator/${project.crop}`} className="quick-action-button kc-button">
          <i className="fas fa-chart-line"></i>
          <span>{t("Kplant Calculator")}</span>
        </Link>
        <button
          onClick={recalculateAll}
          className="quick-action-button recalculate-button"
          disabled={calculationsLoading}
        >
          <i className="fas fa-sync"></i>
          <span>{t("Recalculate All")}</span>
        </button>
      </div>

      {/* Content based on active tab */}
      <div className="project-content">
        {activeTab === "overview" && (
          <div className="overview-tab">
            <div className="overview-grid">
              {/* Farm Details Card */}
              <div className="details-card farm-details">
                <div className="card-header">
                  <i className="fas fa-farm"></i>
                  <h2>{t("Farm Details")}</h2>
                </div>
                <div className="card-body">
                  <div className="detail-item">
                    <span className="detail-label">{t("Farm Name")}:</span>
                    <span className="detail-value">{project.farm.name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t("Farm Code")}:</span>
                    <span className="detail-value">{project.farm.code}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t("Total Farm Area")}:</span>
                    <span className="detail-value">{project.farm.area} m²</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t("Available Farm Area")}:</span>
                    <span className="detail-value">{project.farm.availableArea} m²</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t("Allocated Area")}:</span>
                    <span className="detail-value">{project.allocatedArea} m²</span>
                  </div>
                </div>
              </div>

              {/* Crop Details Card */}
              {cropTypeDetails && (
                <div className="details-card crop-details">
                  <div className="card-header">
                    <i className="fas fa-seedling"></i>
                    <h2>{t("Crop Details")}</h2>
                  </div>
                  <div className="card-body">
                    <div className="detail-item">
                      <span className="detail-label">{t("Crop Type")}:</span>
                      <span className="detail-value">
                        {project.cropDetails?.cropTypeDetails?.name || t("Not specified")}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">{t("Crop")}:</span>
                      <span className="detail-value">{cropTypeDetails.name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">{t("Region")}:</span>
                      <span className="detail-value">{cropTypeDetails.region || t("Not specified")}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">{t("Plant Date")}:</span>
                      <span className="detail-value">
                        {project.cropDetails?.plantMonth
                          ? `1 ${new Date(0, project.cropDetails.plantMonth - 1).toLocaleString("default", { month: "long" })}`
                          : t("Not specified")}
                      </span>
                    </div>

                    <h3 className="section-title">{t("Growth Stages")}</h3>
                    <div className="growth-stages-grid">
                      <div className="growth-stage">
                        <span className="stage-name">{t("Initial")}</span>
                        <span className="stage-value">
                          {cropTypeDetails.growthStages.initial || cropTypeDetails.growthStages.initialDays} {t("days")}
                        </span>
                      </div>
                      <div className="growth-stage">
                        <span className="stage-name">{t("Development")}</span>
                        <span className="stage-value">
                          {cropTypeDetails.growthStages.development || cropTypeDetails.growthStages.developmentDays}{" "}
                          {t("days")}
                        </span>
                      </div>
                      <div className="growth-stage">
                        <span className="stage-name">{t("Mid-Season")}</span>
                        <span className="stage-value">
                          {cropTypeDetails.growthStages.mid || cropTypeDetails.growthStages.midSeasonDays} {t("days")}
                        </span>
                      </div>
                      <div className="growth-stage">
                        <span className="stage-name">{t("Late Season")}</span>
                        <span className="stage-value">
                          {cropTypeDetails.growthStages.late || cropTypeDetails.growthStages.lateSeasonDays} {t("days")}
                        </span>
                      </div>
                      <div className="growth-stage total">
                        <span className="stage-name">{t("Total")}</span>
                        <span className="stage-value">
                          {cropTypeDetails.total ||
                            (cropTypeDetails.growthStages.initial || cropTypeDetails.growthStages.initialDays || 0) +
                            (cropTypeDetails.growthStages.development ||
                              cropTypeDetails.growthStages.developmentDays ||
                              0) +
                            (cropTypeDetails.growthStages.mid || cropTypeDetails.growthStages.midSeasonDays || 0) +
                            (cropTypeDetails.growthStages.late ||
                              cropTypeDetails.growthStages.lateSeasonDays ||
                              0)}{" "}
                          {t("days")}
                        </span>
                      </div>
                    </div>

                    <h3 className="section-title">{t("Crop Coefficients")}</h3>
                    <div className="coefficients-grid">
                      <div className="coefficient">
                        <span className="coefficient-name">{t("Kc ini")}</span>
                        <span className="coefficient-value">{cropTypeDetails.cropCoefficients.initial}</span>
                      </div>
                      <div className="coefficient">
                        <span className="coefficient-name">{t("Kc mid")}</span>
                        <span className="coefficient-value">{cropTypeDetails.cropCoefficients.mid}</span>
                      </div>
                      <div className="coefficient">
                        <span className="coefficient-name">{t("Kc end")}</span>
                        <span className="coefficient-value">{cropTypeDetails.cropCoefficients.end}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
{parameterGroupDetails && (
  <div className="parameter-group-details">
    <h2>{t("Parameter Station Details")}</h2>
    <button
        className="action-button"
        style={{ marginTop: 8 }}
        onClick={handleOpenForecastModal}
    >
      <i className="fas fa-table"></i> {t("View Forecasting Parameter Data")}
    </button>
    <div className="details-card">
      <div className="card-body">
        <div className="detail-item">
          <span className="detail-label">{t("Station Name")}:</span>
          <span className="detail-value">{parameterGroupDetails.name}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t("Group ID")}:</span>
          <span className="detail-value">{parameterGroupDetails.groupId}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t("Location")}:</span>
          <span className="detail-value">
            {parameterGroupDetails.location?.coordinates?.length === 2
              ? `${parameterGroupDetails.location.coordinates[1].toFixed(4)}, ${parameterGroupDetails.location.coordinates[0].toFixed(4)}`
              : t("No location available")}
          </span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t("Active")}:</span>
          <span className="detail-value">{parameterGroupDetails.active ? t("Yes") : t("No")}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t("Created At")}:</span>
          <span className="detail-value">{new Date(parameterGroupDetails.createdAt).toLocaleString()}</span>
        </div>
        <div className="detail-item">
          <span className="detail-label">{t("Updated At")}:</span>
          <span className="detail-value">{new Date(parameterGroupDetails.updatedAt).toLocaleString()}</span>
        </div>

        <h3 className="section-title">{t("Selected Devices")}</h3>
        <ul className="device-list">
          {parameterGroupDetails.selectedDevices?.length > 0 ? (
            parameterGroupDetails.selectedDevices.map((device) => (
              <li key={device._id} className="device-item">
                <p>
                  <strong>{t("Device Name")}:</strong> {device.name}
                </p>
                <p>
                  <strong>{t("Device Code")}:</strong> {device.code}
                </p>
                <p>
                  <strong>{t("Height")}:</strong> {device.height} m
                </p>
                <p>
                  <strong>{t("Active")}:</strong> {device.active ? t("Yes") : t("No")}
                </p>
                <p>
                  <strong>{t("Created At")}:</strong> {new Date(device.createdAt).toLocaleString()}
                </p>
                <p>
                  <strong>{t("Updated At")}:</strong> {new Date(device.updatedAt).toLocaleString()}
                </p>
                <h5>{t("Selected Parameters")}</h5>
                <ul>
                  {device.selectedParameters?.length > 0 ? (
                    device.selectedParameters.map((param) => <li key={param}>{param}</li>)
                  ) : (
                    <li>{t("No parameters selected")}</li>
                  )}
                </ul>
              </li>
            ))
          ) : (
            <li>{t("No devices selected")}</li>
          )}
        </ul>

        <h3 className="section-title">{t("Forecasting Parameters")}</h3>
        <ul className="api-params-list">
          {parameterGroupDetails.apiParams?.length > 0 ? (
            parameterGroupDetails.apiParams.map((param) => <li key={param}>{param}</li>)
          ) : (
            <li>{t("No API parameters available")}</li>
          )}
        </ul>
      </div>
    </div>
  </div>
)}
              {/* Soil Details Card */}
              {soilTypeDetails && (
                <div className="details-card soil-details">
                  <div className="card-header">
                    <i className="fas fa-mountain"></i>
                    <h2>{t("Soil Details")}</h2>
                  </div>
                  <div className="card-body">
                    <div className="detail-item">
                      <span className="detail-label">{t("Soil Type")}:</span>
                      <span className="detail-value">{soilTypeDetails.name}</span>
                    </div>
                    {soilTypeDetails.description && (
                      <div className="detail-item">
                        <span className="detail-label">{t("Description")}:</span>
                        <span className="detail-value">{soilTypeDetails.description}</span>
                      </div>
                    )}

                    <h3 className="section-title">{t("Soil Properties")}</h3>
                    <div className="soil-properties-grid">
                      <div className="soil-property">
                        <span className="property-name">{t("Texture")}</span>
                        <span className="property-value">{t(soilTypeDetails.properties.texture)}</span>
                      </div>
                      <div className="soil-property">
                        <span className="property-name">{t("Drainage Rate")}</span>
                        <span className="property-value">{t(soilTypeDetails.properties.drainageRate)}</span>
                      </div>
                      <div className="soil-property">
                        <span className="property-name">{t("Water Holding Capacity")}</span>
                        <span className="property-value">{t(soilTypeDetails.properties.waterHoldingCapacity)}</span>
                      </div>
                      <div className="soil-property">
                        <span className="property-name">{t("Organic Matter Content")}</span>
                        <span className="property-value">{t(soilTypeDetails.properties.organicMatterContent)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Uploaded Data for Station Selected Parameters */}
              {parameterGroupDetails?.apiParams?.length > 0 && (
                <div className="details-card" style={{ marginTop: 16 }}>
                  <div className="card-header">
                    <i className="fas fa-upload"></i>
                    <h2>{t("Upload Data for Station Selected Parameters")}</h2>
                  </div>
                  <div className="card-body">
                    <div style={{marginBottom: 8}}>
                      <input
                          type="file"
                          accept=".csv"
                          onChange={handleCsvUpload}
                          style={{marginBottom: 8}}
                      />
                      <a
                          href={`data:text/csv;charset=utf-8,${encodeURIComponent(getSampleCsv())}`}
                          download={`sample_station_data_${getRequiredDates().length}_days.csv`}
                      >
                        {t("Download Sample CSV")}
                      </a>
                    </div>
                    <div>
                      <strong>{t("Selected Parameters")}:</strong>
                      <ul>
                        {parameterGroupDetails.selectedDevices?.length > 0 ? (
                          parameterGroupDetails.selectedDevices.flatMap(device =>
                            device.selectedParameters?.length > 0
                              ? device.selectedParameters.map(param => <li key={device._id + param}>{param}</li>)
                              : [<li key={device._id + "_none"}>{t("No parameters selected")}</li>]
                          )
                        ) : (
                          <li>{t("No devices selected")}</li>
                        )}
                      </ul>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <strong>{t("Required Dates")}:</strong>
                      <span style={{ marginLeft: 8 }}>
                        {getRequiredDates().length > 0
                          ? `${getRequiredDates()[0]} - ${getRequiredDates().slice(-1)[0]} (${getRequiredDates().length} ${t("days")})`
                          : t("No date range available")}
                      </span>
                    </div>
                    <div style={{ marginTop: 8, display: "flex", gap: "12px", flexWrap: "wrap" }}>
                      <button
                        className="action-button"
                        onClick={() => setShowCsvModal(true)}
                        disabled={!csvData}
                        style={{ padding: "6px 16px", marginTop: 8 }}
                      >
                        <i className="fas fa-table"></i> {t("View Uploaded Data")}
                      </button>
                      <button
                        className="action-button"
                        onClick={handleSubmitCsvData}
                        disabled={!csvData || !forecastData || csvSubmitLoading}
                        style={{ padding: "6px 16px", marginTop: 8, background: "#10b981", color: "#fff" }}
                      >
                        {csvSubmitLoading ? (
                          <span>
                            <i className="fas fa-spinner fa-spin"></i> {t("Submitting...")}
                          </span>
                        ) : (
                          <span>
                            <i className="fas fa-paper-plane"></i> {t("Submit Data")}
                          </span>
                        )}
                      </button>
                    </div>
                    {csvError && (
                      <div style={{ color: "red", marginTop: 8 }}>{csvError}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "calculations" && (
          <div className="calculations-tab">
            <div className="calculations-header">
              <h2>{t("Project Calculations")}</h2>
              {calculationsLoading && <div className="spinner"></div>}
            </div>

            {etrefCalculations.length > 0 && (
              <div className="details-card etref-calculations">
                <div className="card-header">
                  <i className="fas fa-calculator"></i>
                  <h3>{t("WATref Calculations")}</h3>
                </div>
                <div className="card-body">
                  <div className="calculations-table">
                    <table>
                      <thead>
                        <tr>
                          <th>{t("Date")}</th>
                          <th>{t("WATref (mm/day)")}</th>
                          <th>{t("Model")}</th>
                          <th>{t("Temperature (°C)")}</th>
                          <th>{t("Humidity (%)")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {etrefCalculations.slice(0, 10).map((calc, index) => (
                          <tr key={index}>
                            <td>{new Date(calc.date).toLocaleDateString()}</td>
                            <td>{calc.etref.toFixed(2)}</td>
                            <td>{calc.model}</td>
                            <td>
                              {calc.weatherData ? `${calc.weatherData.tempMax}/${calc.weatherData.tempMin}` : "N/A"}
                            </td>
                            <td>{calc.weatherData ? calc.weatherData.humidity : "N/A"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {etrefCalculations.length > 10 && (
                      <p className="table-note">
                        {t("Showing first 10 records of")} {etrefCalculations.length}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {etplantCalculations.length > 0 && (
              <div className="details-card etplant-calculations">
                <div className="card-header">
                  <i className="fas fa-leaf"></i>
                  <h3>{t("ETplant Calculations")}</h3>
                </div>
                <div className="card-body">
                  <div className="calculations-table">
                    <table>
                      <thead>
                        <tr>
                          <th>{t("Date")}</th>
                          <th>{t("ETplant (mm/day)")}</th>
                          <th>{t("WATref (mm/day)")}</th>
                          <th>{t("Kc")}</th>
                          <th>{t("Growth Stage")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {etplantCalculations.slice(0, 10).map((calc, index) => (
                          <tr key={index}>
                            <td>{new Date(calc.date).toLocaleDateString()}</td>
                            <td>{calc.etplant.toFixed(2)}</td>
                            <td>{calc.etref.toFixed(2)}</td>
                            <td>{calc.kc.toFixed(3)}</td>
                            <td>{t(calc.growthStage)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {etplantCalculations.length > 10 && (
                      <p className="table-note">
                        {t("Showing first 10 records of")} {etplantCalculations.length}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {etrefCalculations.length === 0 && etplantCalculations.length === 0 && !calculationsLoading && (
              <div className="no-calculations">
                <i className="fas fa-info-circle"></i>
                <p>
                  {t("No calculations available. Click the calculate buttons to generate WATref and ETplant values.")}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "kc" && (
          <div className="kc-tab">
            <div className="kc-header">
              <h2>{t("Kplant Values")}</h2>
            </div>

            {kcValues.length > 0 ? (
              <div className="details-card kc-values">
                <div className="card-header">
                  <i className="fas fa-chart-line"></i>
                  <h3>{t("Kplant Values")}</h3>
                </div>
                <div className="card-body">
                  <div className="calculations-table">
                    <table>
                      <thead>
                        <tr>
                          <th>{t("Date")}</th>
                          <th>{t("KC Value")}</th>
                          <th>{t("Growth Stage")}</th>
                          <th>{t("Days Since Planting")}</th>
                          <th>{t("Calculated At")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kcValues.map((kcValue, index) => (
                          <tr key={index}>
                            <td>{new Date(kcValue.date).toLocaleDateString()}</td>
                            <td>{kcValue.kc}</td>
                            <td>{t(kcValue.stage)}</td>
                            <td>{kcValue.daysSincePlanting}</td>
                            <td>{new Date(kcValue.calculatedAt).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-calculations">
                <i className="fas fa-info-circle"></i>
                <p>{t("No KC values available. Use the Kplant Calculator to generate values.")}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showETrefModal && (
        <ETrefCalculationModal
          isOpen={showETrefModal}
          onClose={() => setShowETrefModal(false)}
          onCalculate={handleETrefCalculation}
          farmId={project.farm._id}
          projectId={id}
        />
      )}

      {/* Show fetched weather params for API params */}
      {fetchedWeatherParams && (
        <div className="details-card" style={{ margin: "16px 0", border: "2px solid #2196f3" }}>
          <div className="card-header">
            <i className="fas fa-cloud-sun"></i>
            <h3>{t("Weather Data Used for Calculation")}</h3>
          </div>
          <div className="card-body">
            <ul>
              {parameterGroupDetails?.apiParams?.map((param) => (
                <li key={param}>
                  <strong>{param}:</strong>{" "}
                  {fetchedWeatherParams[param] !== undefined
                    ? <span style={{ color: "#1976d2" }}>{fetchedWeatherParams[param]}</span>
                    : <span style={{ color: "#888" }}>{t("Not available")}</span>}
                </li>
              ))}
            </ul>
            <div style={{ fontSize: "0.95em", color: "#666", marginTop: 8 }}>
              {t("These values were fetched from Open-Meteo and used for the WATref calculation.")}
            </div>
          </div>
        </div>
      )}

      {/* CSV Data Modal (move to bottom of component for clarity) */}
      {showCsvModal && (
        <Modal isOpen={showCsvModal} onClose={() => setShowCsvModal(false)} title={t("Uploaded Data")}>
          {csvData ? (
            <div style={{ overflowX: "auto" }}>
              <table className="csv-table" style={{ minWidth: 400, borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th>Date</th>
                    {parameterGroupDetails.apiParams.map((param) => (
                      <th key={param} style={{ border: "1px solid #ccc", padding: "4px 8px" }}>{param}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvData.map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ border: "1px solid #eee", padding: "4px 8px" }}>{row.date}</td>
                      {parameterGroupDetails.apiParams.map((param) => (
                        <td key={param} style={{ border: "1px solid #eee", padding: "4px 8px" }}>
                          {row[param] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div>{t("No data uploaded yet.")}</div>
          )}
        </Modal>
      )}

      {/* Forecasting Parameter Data Modal */}
      {showForecastModal && (
        <Modal
          isOpen={showForecastModal}
          onClose={() => setShowForecastModal(false)}
          title={t("Forecasting Parameter Data")}
        >
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="avgYearsSelect" style={{ marginRight: 8 }}>
              {t("Average Years")}:
            </label>
            <select
              id="avgYearsSelect"
              value={forecastAvgYears}
              onChange={async (e) => {
                const years = parseInt(e.target.value, 10);
                setForecastAvgYears(years);
                await handleViewForecastParams(years);
              }}
              style={{ padding: "4px 12px" }}
            >
              {[1, 3, 5, 7, 10].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          {forecastLoading ? (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <div className="spinner-large"></div>
              <div>{t("Loading forecasting data...")}</div>
            </div>
          ) : forecastData && forecastData.length > 0 ? (
            <div style={{ overflowX: "auto", maxHeight: "60vh" }}>
              <table className="csv-table" style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    {Object.keys(forecastData[0]).map((key) => (
                      <th key={key}>{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {forecastData.map((row, idx) => (
                    <tr key={idx}>
                      {Object.keys(row).map((key) => (
                        <td key={key}>{row[key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div>{t("No forecasting data available.")}</div>
          )}
        </Modal>
      )}
    </div>
  )
}

export default ProjectDetails
